import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { withTranslation } from 'react-i18next';

import { SlateDialog } from 'components/Shared/SlateDialog';
import p from 'theme/palette';

const MaterialSymbol = ({ name, style = {}, className = '' }) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={{ fontSize: 20, lineHeight: 1, ...style }}
  >
    {name}
  </span>
);

const useStyles = makeStyles(() => ({
  content: {
    padding: '4px 0',
  },
  description: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 13,
    lineHeight: 1.6,
    color: p.grey600,
    marginBottom: 24,
  },
  urlContainer: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    marginBottom: 4,
  },
  urlField: {
    flex: 1,
    padding: '12px 14px',
    backgroundColor: p.grey200,
    borderRadius: '8px 0 0 8px',
    border: `1px solid ${p.border}`,
    borderRight: 'none',
    fontFamily: "'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace",
    fontSize: 12,
    color: p.defaultText,
    lineHeight: 1.5,
    wordBreak: 'break-all',
    userSelect: 'all',
    cursor: 'text',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      borderColor: p.grey400,
    },
    '&:focus': {
      outline: 'none',
      borderColor: p.primary,
      backgroundColor: p.white,
    },
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
    backgroundColor: p.primary,
    border: 'none',
    borderRadius: '0 8px 8px 0',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: p.primaryH,
    },
    '&:active': {
      backgroundColor: '#0f3d73',
    },
  },
  copyButtonSuccess: {
    backgroundColor: p.success,
    '&:hover': {
      backgroundColor: '#047857',
    },
    '&:active': {
      backgroundColor: '#065f46',
    },
  },
  copyIcon: {
    fontSize: 20,
    color: p.white,
  },
}));

const ShareModal = ({ open, onClose, shareUrl, t }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    copy(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  const handleUrlClick = useCallback((e) => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(e.target);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const handleClose = useCallback(() => {
    setCopied(false);
    onClose();
  }, [onClose]);

  return (
    <SlateDialog
      open={open}
      onClose={handleClose}
      title="Share This View"
    >
      <div className={classes.content}>
        <p className={classes.description}>
          {t('slateView:publicView')}
        </p>

        <div className={classes.urlContainer}>
          <div
            className={classes.urlField}
            onClick={handleUrlClick}
            role="textbox"
            tabIndex={0}
            aria-readonly="true"
            aria-label="Share URL"
          >
            {shareUrl}
          </div>
          <button
            type="button"
            className={`${classes.copyButton} ${copied ? classes.copyButtonSuccess : ''}`}
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy link'}
          >
            <MaterialSymbol
              name={copied ? 'check' : 'content_copy'}
              className={classes.copyIcon}
            />
          </button>
        </div>
      </div>
    </SlateDialog>
  );
};

ShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shareUrl: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ShareModal);
